import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { FiPlay } from 'react-icons/fi'
import { usePublicContent } from '../hooks/usePublicContent'
import { VideoSectionContent } from '../types/content'
import { useVideoThumbnail } from '../hooks/useVideoThumbnail'

// Component to render video thumbnail
const VideoThumbnailDisplay: React.FC<{ videoUrl: string; title: string }> = ({ videoUrl, title }) => {
  const { thumbnail, isLoading } = useVideoThumbnail(videoUrl, {
    timeOffset: 2,
    quality: 0.8,
    width: 1920,
    height: 1080
  })

  if (isLoading) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 animate-pulse" />
    )
  }

  if (thumbnail) {
    return (
      <img
        src={thumbnail}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
      />
    )
  }

  return (
    <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary" />
  )
}

const VideoSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })
  
  // Get video content from CMS
  const { content, loading } = usePublicContent('videos')
  const videoData = content?.data as VideoSectionContent
  
  const [isMobile, setIsMobile] = useState(false)
  const [currentVideoId, setCurrentVideoId] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Use CMS videos data, sorted by order
  const videos = videoData?.videos?.sort((a, b) => a.order - b.order) || []
  
  // Set initial video when data loads
  useEffect(() => {
    if (videos.length > 0 && !currentVideoId) {
      setCurrentVideoId(videos[0].id)
    }
  }, [videos, currentVideoId])

  // Get the current video to display
  const featuredVideo = currentVideoId 
    ? videos.find(v => v.id === currentVideoId) 
    : videos[0]

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Show loading state
  if (loading) {
    return (
      <section id="music" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="animate-pulse bg-neutral-200 h-12 w-64 mx-auto mb-6 rounded"></div>
            <div className="animate-pulse bg-neutral-200 h-4 w-3/4 mx-auto rounded"></div>
          </div>
          <div className="grid md:grid-cols-[20rem_1fr] xl:grid-cols-[24rem_1fr] gap-8 xl:gap-12">
            <div className="hidden md:block">
              <div className="animate-pulse bg-neutral-200 h-96 rounded-2xl"></div>
            </div>
            <div className="animate-pulse bg-neutral-200 aspect-video rounded-2xl"></div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section 
      ref={ref}
      id="music" 
      className="section-padding bg-white"
    >
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 px-4"
        >
          <h2 className="font-bold text-neutral-800 mb-4 sm:mb-6" style={{ fontSize: 'clamp(28px, 4vw, 48px)' }}>
            {videoData?.title || 'Live'} <span className="gradient-text">{videoData?.subtitle || 'Performances'}</span>
          </h2>
          <p className="body-longform text-neutral-600 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
            {videoData?.description || 'Watch Big Swing Energy in action at exclusive venues across the country. Experience the energy and talent that makes every performance unforgettable.'}
          </p>
        </motion.div>

        {/* Desktop two-column layout */}
        <div className="grid md:grid-cols-[20rem_1fr] xl:grid-cols-[24rem_1fr] gap-6 sm:gap-8 xl:gap-12">
          {/* Left column - Video list (desktop only) */}
          <div className="hidden md:block">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="card"
              style={{ height: '27.5rem' }}
            >
              <div className="h-full flex flex-col">
                <h3 className="text-lg sm:text-xl font-bold text-neutral-800 mb-4 sm:mb-6">Select Performance</h3>
                <div className="flex-1 space-y-2 sm:space-y-3 overflow-y-auto overflow-x-hidden">
                  {videos.map((video) => (
                    <button
                      key={video.id}
                      onClick={() => setCurrentVideoId(video.id)}
                      className={`w-full text-left p-3 sm:p-4 rounded-xl transition-all duration-200 min-h-[44px] ${
                        currentVideoId === video.id
                          ? 'bg-primary/10 border-2 border-primary'
                          : 'bg-neutral-50 border border-neutral-200 hover:bg-neutral-100'
                      }`}
                    >
                      <div className="font-semibold text-neutral-800 mb-1 text-sm sm:text-base">
                        {video.title}
                      </div>
                      <div className="text-xs sm:text-sm text-neutral-600">
                        {video.artist} • {video.runtime}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right column - Featured Video */}
          {featuredVideo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col"
            >
              <div 
                className="relative aspect-video w-full rounded-xl sm:rounded-2xl overflow-hidden shadow-xl group cursor-pointer transition-all duration-300 hover:shadow-2xl bg-neutral-900"
                onClick={() => setIsModalOpen(true)}
              >
                {/* Video thumbnail - generated from actual video */}
                <VideoThumbnailDisplay 
                  videoUrl={featuredVideo.videoUrl} 
                  title={featuredVideo.title}
                />
                
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-all duration-300">
                  <motion.div 
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform"
                    whileHover={{ scale: 1.1 }}
                  >
                    <FiPlay className="w-6 h-6 sm:w-8 sm:h-8 ml-1 text-neutral-900" />
                  </motion.div>
                </div>
                
                {/* Video info overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-neutral-900/90 via-neutral-900/60 to-transparent p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1">
                    {featuredVideo.title}
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg text-white/90">
                    {featuredVideo.artist} • {featuredVideo.runtime}
                  </p>
                </div>
              </div>
              
              {/* Video description/runtime */}
              <div className="mt-4 sm:mt-6 flex items-center justify-between">
                <p className="body-longform text-neutral-600">
                  
                </p>
                
              </div>
            </motion.div>
          )}
        </div>

        {/* Mobile video list - shown below video */}
        {isMobile && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="md:hidden mt-6 sm:mt-8"
          >
            <div className="card">
              <h3 className="text-lg sm:text-xl font-bold text-neutral-800 mb-4 sm:mb-6">Select Performance</h3>
              <div className="space-y-2 sm:space-y-3">
                {videos.map((video) => (
                  <motion.button
                    key={video.id}
                    onClick={() => setCurrentVideoId(video.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-left p-3 sm:p-4 rounded-xl transition-all duration-200 min-h-[44px] ${
                      currentVideoId === video.id
                        ? 'bg-primary/10 border-2 border-primary'
                        : 'bg-neutral-50 border border-neutral-200 hover:bg-neutral-100'
                    }`}
                  >
                    <div className="font-semibold text-neutral-800 mb-1 text-sm sm:text-base">
                      {video.title}
                    </div>
                    <div className="text-xs sm:text-sm text-neutral-600">
                      {video.artist} • {video.runtime}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Video Modal */}
        {isModalOpen && featuredVideo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-4xl">
              <motion.button
                onClick={() => setIsModalOpen(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="absolute -top-10 sm:-top-12 right-0 text-white hover:text-primary transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
              <div className="aspect-video bg-neutral-900 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                <video
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  src={featuredVideo.videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default VideoSection
